import { useState } from 'react';

import type { AnalysisOutput } from '@smartapply/shared';

import { SmartApplyApiError } from '../../lib/api-client.js';
import { saveAnalysis } from '../../lib/analyses-client.js';
import { useRoute } from '../../lib/route.js';

interface SaveAnalysisBoxProps {
  output: AnalysisOutput;
  pdfBytes?: number;
  pdfPages?: number;
}

/**
 * Bloco opt-in pra salvar a análise. Espelha o padrão usado nos drafts
 * (DraftSetView): label opcional, persiste sob clique, mostra link pra detalhe.
 */
export function SaveAnalysisBox({ output, pdfBytes, pdfPages }: SaveAnalysisBoxProps) {
  const { navigate } = useRoute();
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    setError(null);
    setSaving(true);
    try {
      const result = await saveAnalysis({
        output,
        pdfBytes: pdfBytes ?? null,
        pdfPages: pdfPages ?? null,
        label: label || undefined,
      });
      setSavedId(result.id);
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao salvar.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="card-paper rounded-sm px-6 py-5"
      style={{ borderLeft: '3px solid var(--accent-forest)' }}
    >
      {savedId ? (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="font-sans text-sm text-[var(--accent-forest)]">
            ✓ Análise salva no histórico.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/analises/${savedId}`)}
              className="font-sans text-xs text-[var(--ink-secondary)] underline underline-offset-4"
            >
              ver detalhe
            </button>
            <button
              type="button"
              onClick={() => navigate('/analises')}
              className="rounded-sm bg-[var(--ink-primary)] px-4 py-1.5 font-sans text-xs text-[var(--bg-paper)]"
            >
              ver todas
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            salvar no histórico (opcional)
          </p>
          <p className="mt-2 font-sans text-sm text-[var(--ink-secondary)]">
            Por padrão, a análise vive só nesta sessão. Se você fechar a aba, ela some. Salve
            pra revisitar depois ou comparar com versões futuras do seu perfil.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="rótulo (opcional, ex: 'antes da reescrita')"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={120}
              className="min-w-[220px] flex-1 rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-sans text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
            />
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="rounded-sm bg-[var(--ink-primary)] px-4 py-2 font-sans text-sm text-[var(--bg-paper)] hover:opacity-90 disabled:opacity-40"
            >
              {saving ? 'Salvando...' : 'Salvar análise'}
            </button>
          </div>
          {error && (
            <p className="mt-3 font-sans text-xs text-[var(--accent-crimson)]">{error}</p>
          )}
        </>
      )}
    </div>
  );
}
